import {
  FmlxTableBody,
  FmlxTableContainer,
  FmlxTableHead,
  FmlxTableHeadCell,
  FmlxTableRow,
} from 'fmlx-common-ui';
import React, { useMemo } from 'react';
import { CaptureProfileOption } from './ExperimentReportOptions';
import HitsReportCaptureProfileTableRow from './HitsReportCaptureProfileTableRow';
import { HitsReportCaptureProfileColumnDef } from './HitsReportCaptureProfileTable.types';

export type { HitsReportCaptureProfileColumnDef };

export type HitsReportCaptureProfileTableProps = {
  id: string;
  columns: HitsReportCaptureProfileColumnDef[];
  captureProfiles: CaptureProfileOption[];
  onProfileChange: (checked: boolean, profile: CaptureProfileOption) => void;
  customHeight?: string;
  customWidth?: string;
};

export const HitsReportCaptureProfileTable = ({
  id,
  columns,
  captureProfiles,
  onProfileChange,
  customHeight,
  customWidth,
}: HitsReportCaptureProfileTableProps) => {
  const selectedCount = useMemo(
    () => captureProfiles.filter((cp) => cp.selected).length,
    [captureProfiles],
  );

  const tableHeaders = useMemo(
    () => (
      <FmlxTableHead
        id={`${id}-fmlxtablehead`}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <FmlxTableRow
          id={`${id}-fmlxtablerow-header`}
          onClick={null}
          sx={{ borderBottom: 'none', '& th': { borderBottom: 'none' } }}
        >
          {columns.map((column, idx) => (
            <FmlxTableHeadCell
              id={`${id}-fmlxtableheadcell-column-${column.id}`}
              key={`fmlx-table-head-cell-${idx}`}
              label={column.title}
              align={column.textAlign ?? 'left'}
              sortDisabled
              onClickSort={null}
              size="small"
              className="sticky top-0 z-10 text-nowrap text-xs"
              sx={{ borderBottom: 'none' }}
            />
          ))}
        </FmlxTableRow>
      </FmlxTableHead>
    ),
    [id, columns],
  );

  const tableBody = useMemo(
    () => (
      <FmlxTableBody
        id={`${id}-fmlxtablebody`}
        sx={{
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        {captureProfiles.map((profile, rowIndex) => (
          <HitsReportCaptureProfileTableRow
            key={`hits-report-capture-profile-row-${profile.id}`}
            id={id}
            rowIndex={rowIndex}
            profile={profile}
            columns={columns}
            selectedCount={selectedCount}
            onProfileChange={onProfileChange}
          />
        ))}
      </FmlxTableBody>
    ),
    [id, captureProfiles, columns, selectedCount, onProfileChange],
  );

  return (
    <FmlxTableContainer
      id={`${id}-fmlxtablecontainer`}
      stickyHeader
      size="small"
      classNames={{
        base: 'hits-report-capture-profile-table',
        content: '',
      }}
      sxs={{
        base: {
          maxHeight: '100%',
          minHeight: '20%',
          height: customHeight ?? '100%',
          width: customWidth ?? '100%',
        },
      }}
    >
      {tableHeaders}
      {tableBody}
    </FmlxTableContainer>
  );
};
